'use client'

import { useEffect, useRef, useCallback, useMemo } from 'react'

import { partition } from 'lodash'
import { useRouter, useSearchParams } from 'next/navigation'

import { TransactionFeedView } from '~/components/TransactionFeedView'
import { DialogWrapper } from '~/components/forms/transfer/dialog/DialogWrapper'
import { ApproveTransactionDialogContent } from '~/components/smart-accounts/ApproveTransactionDialogContent'
import { BatchTransactionDialogContent } from '~/components/smart-accounts/BatchTransactionDialogContent'
import { InboundTransactionDialogContent } from '~/components/smart-accounts/InboundTransactionDialog'
import { TransactionDetailDialogContent } from '~/components/smart-accounts/TransactionDetailDialogContent'
import { TransactionFeedItem } from '~/components/smart-accounts/transactions/TransactionFeedItem'
import { SHOW_SMALL_TRANSFERS_KEY } from '~/constants/localStorage'
import { SUBMITTED_TRANSACTION_STATUSES } from '~/constants/smart_accounts'
import { useAuth } from '~/context/AuthProvider'
import { useNavigation } from '~/hooks/useNavigation'
import { useShowDust } from '~/hooks/useShowDust'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useTransactions } from '~/hooks/useTransactions'
import { OrgAssetTransfer, OrgTransaction, TransactionStatus } from '~/types'
import { EvmAddress } from '~/utils/address'
import { isDraftBatchTransaction } from '~/utils/smartAccounts/batch'

export const TransactionFeed = ({
  smartAccountId,
  smartAccountOrgId,
}: {
  smartAccountId?: string
  smartAccountOrgId?: string
}) => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()

  const router = useRouter()
  const searchParams = useSearchParams()
  const transactionId = searchParams.get('transactionId')
  const pendingTransactionIdRef = useRef<string | null>(null)
  const navigate = useNavigation()
  const [showDust] = useShowDust(SHOW_SMALL_TRANSFERS_KEY)

  const { transactions, hasMore, isInitialLoading, fetchNextPage } =
    useTransactions({
      smartAccountId,
      smartAccountOrgId,
      hideDust: !showDust,
    })

  const displayTransactions = useMemo(() => {
    const [draftedBatches, rest] = partition(
      transactions,
      (t) => t.isSplitsTransaction && isDraftBatchTransaction(t),
    )
    return [...draftedBatches, ...rest]
  }, [transactions])

  const openTransactionModal = useCallback(
    (transaction: OrgTransaction | OrgAssetTransfer) => {
      if (transaction.isSplitsTransaction) {
        if (SUBMITTED_TRANSACTION_STATUSES.includes(transaction.status)) {
          setGlobalModalState({
            isOpen: true,
            content: (
              <TransactionDetailDialogContent
                transaction={transaction}
                onClose={closeGlobalModal}
              />
            ),
          })
        } else if (isDraftBatchTransaction(transaction)) {
          setGlobalModalState({
            isOpen: true,
            content: (
              <BatchTransactionDialogContent
                transaction={transaction}
                onClose={closeGlobalModal}
              />
            ),
          })
        } else if (transaction.status === TransactionStatus.Drafted) {
          setGlobalModalState({
            isOpen: true,
            content: (
              <DialogWrapper
                isOpen={true}
                setIsOpen={closeGlobalModal}
                defaultSender={transaction.smartAccountAddress}
                defaultRecipients={
                  transaction.transactionRecipients
                    ? transaction.transactionRecipients.map((r) => ({
                        address: EvmAddress.format(r.recipientAddress),
                        amount: r.expectedAmount,
                        compliantPaymentRelationId:
                          r.compliantPaymentRelationId,
                      }))
                    : undefined
                }
                defaultTransferToken={{
                  token: {
                    address: EvmAddress.format(
                      transaction.displayTokenAddress!,
                    ),
                    chainId: transaction.chainId,
                  },
                }}
                defaultMemo={transaction.memo ?? undefined}
                transactionId={transaction.id}
              />
            ),
          })
        } else {
          setGlobalModalState({
            isOpen: true,
            content: (
              <ApproveTransactionDialogContent
                transaction={transaction}
                onClose={closeGlobalModal}
              />
            ),
          })
        }
      } else {
        setGlobalModalState({
          isOpen: true,
          content: (
            <InboundTransactionDialogContent
              assetTransfer={transaction}
              onClose={closeGlobalModal}
            />
          ),
        })
      }
    },
    [setGlobalModalState, closeGlobalModal],
  )

  // First effect: If there is a transactionId search param, cache it and remove it from the URL
  useEffect(() => {
    if (transactionId && transactionId !== pendingTransactionIdRef.current) {
      const url = new URL(window.location.href)
      url.searchParams.delete('transactionId')
      window.history.pushState({}, '', url.toString())

      pendingTransactionIdRef.current = transactionId
    }
  }, [transactionId, router])

  // Second effect: Select transaction after URL is updated
  useEffect(() => {
    if (pendingTransactionIdRef.current) {
      const transaction = displayTransactions.find(
        (t) => t.id === pendingTransactionIdRef.current,
      )
      if (transaction) {
        openTransactionModal(transaction)
        pendingTransactionIdRef.current = null
      }
    }
  }, [displayTransactions, openTransactionModal])

  const onClickTransaction = useCallback(
    (transaction: OrgTransaction | OrgAssetTransfer) => {
      if (transaction.isSplitsTransaction) {
        openTransactionModal(transaction)
      } else {
        if ('memo' in transaction) {
          openTransactionModal(transaction)
        } else {
          navigate(
            `/accounts/${transaction.smartAccountAddress}/transactions/inbound/${transaction.id}/`,
          )
        }
      }
    },
    [navigate, openTransactionModal],
  )

  // Fetch all smart accounts once and create a lookup map
  const { smartAccounts } = useSmartAccounts()
  const smartAccountsByAddress = useMemo(
    () =>
      new Map(
        smartAccounts.map((account) => [
          EvmAddress.format(account.address),
          account,
        ]),
      ),
    [smartAccounts],
  )

  const renderItem = useCallback(
    (transaction: OrgTransaction | OrgAssetTransfer) => {
      const smartAccount = smartAccountsByAddress.get(
        EvmAddress.format(transaction.smartAccountAddress),
      )
      return (
        <TransactionFeedItem
          transaction={transaction}
          smartAccountId={smartAccountId}
          smartAccount={smartAccount}
          onClick={onClickTransaction}
        />
      )
    },
    [onClickTransaction, smartAccountId, smartAccountsByAddress],
  )

  return (
    <TransactionFeedView
      title="Transactions"
      emptyMessage="There are no transactions"
      isLoading={isInitialLoading}
      list={displayTransactions}
      hasNextPage={hasMore}
      loadNextPage={async () => {
        const { data } = await fetchNextPage()
        return data?.pages.flatMap((page) => page.transactions)
      }}
      renderItem={renderItem}
      hideDustLocalStorageKey={SHOW_SMALL_TRANSFERS_KEY}
    />
  )
}
